import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "../../../components/Flex";

import { Spacing } from "../../../theme/Spacing";
import { Text } from "../../../components/Text";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { ThemeKeys, useTheme } from "../../../ThemeContext";
import omit from "lodash/omit";
import { SettingsWrapper } from "../components/SettingsWrapper";

const ColorDots = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const Dot = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

export const SettingsAppearance: React.FC = () => {
  const { t } = useTranslation();
  const { changeTheme, selectedTheme, allThemes } = useTheme();

  const themeOptions = Object.values(omit(ThemeKeys)).map((theme) => ({
    label: t(`settings.appearance.theme.options.${theme.toLowerCase()}`),
    value: theme,
  }));

  const onChangeTheme = (theme: ThemeKeys) => {
    if (theme === selectedTheme) {
      return;
    }

    changeTheme(theme);
  };

  // v = { label: string, value: ThemeKeys }
  const renderThemeOption = (v: any) => {
    return (
      <Flex alignItems="center" gap="xs" justifyContent="space-between">
        <span>{v.label}</span>
        <ColorDots>
          <Dot color={allThemes[v.value?.toString() as ThemeKeys]?.main} />
          <Dot color={allThemes[v.value?.toString() as ThemeKeys]?.darkMain} />
          <Dot color={allThemes[v.value?.toString() as ThemeKeys]?.lightMain} />
          <Dot color={allThemes[v.value?.toString() as ThemeKeys]?.accent} />
        </ColorDots>
      </Flex>
    );
  };

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>{t("settings.appearance.theme.title")}</Text>

      <Text fontSize="s" color="darkerSub">
        {t("settings.appearance.theme.description")}
      </Text>

      <AtiraSelect
        value={themeOptions.find((o) => o.value === selectedTheme)}
        onChange={(v) => onChangeTheme(v as ThemeKeys)}
        options={themeOptions}
        labelRender={renderThemeOption}
        optionRender={renderThemeOption}
      />
    </SettingsWrapper>
  );
};
